import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header, { scrollToSection } from './Header';
import Footer, { Contact } from './Footer';
import Seo from './Seo';
import { initPageEffects } from '../lib/page-effects';

export default function Page({
  seo,
  jsonLd,
  brand,
  headerFade = false,
  contactIntro,
  children,
}) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [navOpen, setNavOpen] = useState(false);
  const [contactVisible, setContactVisible] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('nav-open', navOpen);
    return () => document.body.classList.remove('nav-open');
  }, [navOpen]);

  useEffect(() => {
    setNavOpen(false);
    setContactVisible(false);
    const section = searchParams.get('section');
    if (section) {
      window.requestAnimationFrame(() => scrollToSection(section, true));
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    let destroyed = false;
    let cleanup = () => {};
    const frame = window.requestAnimationFrame(() => {
      if (destroyed) {
        return;
      }
      cleanup = initPageEffects();
    });
    return () => {
      destroyed = true;
      window.cancelAnimationFrame(frame);
      cleanup();
    };
  }, [location.pathname]);

  function openContact() {
    setContactVisible(true);
    window.requestAnimationFrame(() => scrollToSection('contact', false, 2));
  }

  const brandVars = brand
    ? `:root { --color-brand-primary: ${brand.primary}; --color-brand-foreground: ${brand.foreground || brand.primary}; --color-black: #000; --color-white: #FFF; }`
    : null;

  return (
    <div id="wrapper">
      <Seo
        title={seo.title}
        description={seo.description}
        path={seo.path || location.pathname}
        image={seo.image}
        type={seo.type}
        jsonLd={jsonLd}
      />
      {brandVars ? (
        <Helmet>
          <style>{brandVars}</style>
        </Helmet>
      ) : null}
      <Header
        navOpen={navOpen}
        fade={headerFade}
        onToggleNav={() => setNavOpen((open) => !open)}
        onCloseNav={() => setNavOpen(false)}
      />
      <main>{children}</main>
      <Footer onContact={openContact} />
      <Contact visible={contactVisible} intro={contactIntro} />
    </div>
  );
}
